import { useCreateLearnerNoteMutation } from '@/app/redux/endpoints/learnerNotes';
import { useAppDispatch, useAppSelector } from '@/app/redux/hooks';
import { useCurrentOrganisationId } from '@/app/redux/hooks/organisation';
import { setAttemptedProFeature } from '@/app/redux/slices/modalsSlice';
import { selectUserId } from '@/app/redux/slices/sessionSlice';
import { selectShareLink } from '@/app/redux/slices/shareLinkSlice';
import { PlacementTestResult } from '@/common/types/placementTest';
import { Activity, Session } from '@/common/types/session';
import { StudentEvent } from '@/common/types/studentEvents';
import { FeatureSwitch, useIsFeatureEnabled } from '@/common/ui/FeatureGate';
import ShareLinkDialogue from '@/common/ui/modals/ShareLinkDialogue';
import { UNKNOWN_ERROR_MESSAGE } from '@/common/ui/UnknownErrorMessage';
import { formatTimeTaken } from '@/common/utils/datetime';
import trackMixpanelEvent from '@/common/utils/mixpanel';
import tw from '@/common/utils/twmacro';
import { generateSessionSummaryHTML } from '@/dashboard/LearnerNotes/sessionSummaryContentGenerator';
import ActivityItem from '@/dashboard/LearnerView/ActivityFeed/ActivityItem';
import api from '@/db/apiClient';
import { FileTextIcon, LinkIcon, MagicWandIcon } from '@phosphor-icons/react';
import { Badge } from '@shadcn/components/badge';
import { Button } from '@shadcn/components/button';
import { Skeleton } from '@shadcn/components/skeleton';
import {
  Tooltip,
  TooltipContent,
  TooltipTrigger,
} from '@shadcn/components/tooltip';
import dayjs from 'dayjs';
import { useEffect, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { toast } from 'sonner';
import ActivityDialog from './ActivityDialog';
import PlacementTestDialogue from './PlacementTestDialogue';

interface SessionItemProps {
  sessionId: string;
  studentId: string;
  studentName: string;
  organisationId?: string;
  renderEvent: (event: StudentEvent) => JSX.Element | undefined;
}

export default function SessionItem({
  sessionId,
  studentId,
  studentName,
  organisationId: organisationIdProp,
  renderEvent,
}: SessionItemProps) {
  const dispatch = useAppDispatch();
  const [createLearnerNote] = useCreateLearnerNoteMutation();
  const navigate = useNavigate();
  const isFeatureEnabled = useIsFeatureEnabled();

  const { currentOrganisationId } = useCurrentOrganisationId();
  const organisationId = organisationIdProp ?? currentOrganisationId;

  const userId = useAppSelector(selectUserId);
  const { shareLink } = useAppSelector(selectShareLink);

  const [isGenerating, setIsGenerating] = useState(false);

  const [isLoading, setLoading] = useState(true);
  const [selectedActivity, setSelectedActivity] = useState<Activity | null>(
    null,
  );
  const [prevSelectedActivity, setPrevSelectedActivity] =
    useState<Activity | null>(null);
  const [selectedPlacementTestResult, setSelectedPlacementTestResult] =
    useState<PlacementTestResult | null>(null);

  const [session, setSession] = useState<Session>();
  const [activities, setActivities] = useState<Activity[]>([]);
  const [placementTestResults, setPlacementTestResults] = useState<
    PlacementTestResult[]
  >([]);
  const [studentEvents, setStudentEvents] = useState<StudentEvent[]>([]);
  const [shareLinkDialogueOpen, setShareLinkDialogueOpen] = useState(false);
  const [highlight, setHighlight] = useState(false);

  const handleOpenShareDialog = (
    type: 'session' | 'activity',
    activity?: Activity,
  ) => {
    if (type === 'session') {
      trackMixpanelEvent('Session Share Link Generated', {
        studentId,
        sessionId,
        activityCount: activities.length,
      });
    } else if (activity) {
      trackMixpanelEvent('Activity Share Link Generated', {
        studentId,
        activityId: activity.id,
        activityName: activity.activityName,
      });
    }
    setShareLinkDialogueOpen(true);
  };

  const combinedItems = useMemo(
    () =>
      sortCombinedEvents([
        ...activities,
        ...placementTestResults,
        ...studentEvents,
      ]),
    [activities, placementTestResults, studentEvents],
  );

  useEffect(() => {
    (async () => {
      try {
        setLoading(true);
        const service = api.asShareLink ?? api.asEducator;
        const session = await service.getSession(sessionId);
        setSession(session);
      } catch (e) {
        console.error('Error fetching session:', e);
      } finally {
        setLoading(false);
      }
    })();
  }, [sessionId]);

  useEffect(() => {
    (async () => {
      if (!session || !organisationId) return;
      try {
        setLoading(true);
        const service = api.asShareLink ?? api.asEducator;
        const activities = await service.getActivitiesForSession(
          organisationId,
          session.id,
        );
        setActivities(activities);
        const placementTestResults = await service.getPlacementTestResults(
          organisationId,
          session.id,
        );
        setPlacementTestResults(placementTestResults);
        const events = await service.getEventsForSession(
          organisationId,
          session.id,
        );
        setStudentEvents(events);
      } catch (e) {
        console.error(
          'Error loading activities and placement test results:',
          e,
        );
      } finally {
        setLoading(false);
      }
    })();
  }, [session, organisationId]);

  // Briefly highlight session if from share link
  useEffect(() => {
    let timeout: NodeJS.Timeout | null = null;
    if (
      shareLink &&
      shareLink.type === 'session' &&
      shareLink.resourceId === sessionId
    ) {
      setHighlight(true);
      timeout = setTimeout(() => setHighlight(false), 3000);
    }
    return () => {
      if (timeout) clearTimeout(timeout);
    };
  }, [shareLink, sessionId]);

  // Open activity dialogue if from share link
  useEffect(() => {
    if (shareLink && shareLink.type === 'activity') {
      if (isFeatureEnabled('activity-feed')) {
        const activity = activities.find((a) => a.id === shareLink.resourceId);
        if (activity) setSelectedActivity(activity);
      } else {
        dispatch(setAttemptedProFeature('Rich activity data'));
      }
    }
  }, [shareLink, activities, dispatch, isFeatureEnabled]);

  function isPlacementTestResult(
    item: Activity | PlacementTestResult | StudentEvent | null,
  ): item is PlacementTestResult {
    return !!item && 'placementLevel' in item;
  }

  function isStudentEvent(
    item: Activity | PlacementTestResult | StudentEvent | null,
  ): item is StudentEvent {
    return !!item && 'actor' in item;
  }

  function sortCombinedEvents(
    events: (Activity | PlacementTestResult | StudentEvent)[],
  ) {
    return [...events].sort((a, b) => {
      const getTimestamp = (e: any) =>
        'startedAt' in e ? e.startedAt : e.createdAt;

      return getTimestamp(b) - getTimestamp(a);
    });
  }

  async function handleGenerateSummary() {
    if (
      !session ||
      !currentOrganisationId ||
      !userId ||
      activities.length === 0
    ) {
      console.error('Error generating summary: Missing required fields');
      toast.error('Failed to generate session summary', {
        description: UNKNOWN_ERROR_MESSAGE,
      });
      return;
    }

    setIsGenerating(true);
    try {
      const html = await generateSessionSummaryHTML(
        currentOrganisationId,
        session,
        activities,
      );
      const note = await createLearnerNote({
        createdBy: userId,
        organisationId: currentOrganisationId,
        studentId,
        sessionId: session.id,
        name: `Session Summary - ${dayjs(session.startedAt).format('DD/MM/YYYY')}`,
        content: html,
      }).unwrap();
      trackMixpanelEvent('Session Summary Generated', {
        sessionId: session.id,
        studentId,
        activityCount: activities.length,
      });
      toast.success('Session summary created');
      navigate(`/dashboard/learners/${studentId}/notes/${note.id}`);
    } catch (error) {
      console.error('Failed to generate session summary:', error);
      toast.error('Failed to generate session summary');
    } finally {
      setIsGenerating(false);
    }
  }

  function formatStartedAt(startedAt: number) {
    const ONE_DAY_MS = 1000 * 60 * 60 * 24;
    const now = Date.now();
    if (now - startedAt < ONE_DAY_MS) {
      return dayjs(startedAt).fromNow();
    }
    return dayjs(startedAt).format('DD/MM/YYYY h:mm a');
  }

  function renderItem(
    item: Activity | PlacementTestResult | StudentEvent | null,
  ) {
    if (isStudentEvent(item)) {
      return renderEvent(item);
    } else if (isPlacementTestResult(item)) {
      const totalAttempts = item.wordResponses.length;
      const correctAttempts = item.wordResponses.filter((r) =>
        r.analysis.attemptToCorrectDistances.every((d) => !d),
      ).length;
      const accuracy =
        totalAttempts > 0
          ? Math.round((correctAttempts / totalAttempts) * 100)
          : 0;
      return (
        <ItemContainer
          onClick={() => {
            if (isFeatureEnabled('activity-feed')) {
              setSelectedPlacementTestResult(item);
            } else {
              dispatch(setAttemptedProFeature('Rich activity data'));
            }
          }}
        >
          <div className="flex flex-col">
            <div className="text-xs text-gray-400 mb-auto">
              {dayjs(item.startedAt).format('DD/MM/YYYY h:mm a')}
            </div>

            <div className="text-sm font-medium flex items-center gap-2">
              Placement Test
              <Tooltip>
                <TooltipTrigger>
                  <Badge variant="outline">Legacy</Badge>
                </TooltipTrigger>
                <TooltipContent align="start">
                  Placement Tests have been replaced with Assessments. We have
                  retained existing Placement Test results for your use.
                </TooltipContent>
              </Tooltip>
            </div>
            <div className="text-xs text-muted-foreground">
              Placed at Level {item.placementLevel}
            </div>
          </div>

          <div className="flex flex-col text-right">
            <div className="text-base">{accuracy}%</div>
            <div className="text-xs text-muted-foreground">Accuracy</div>
            <div className="text-xs text-muted-foreground mt-1">
              Time Taken: {formatTimeTaken(item.startedAt, item.endedAt)}
            </div>
          </div>
        </ItemContainer>
      );
    } else if (item) {
      return (
        <ItemContainer
          onClick={() => {
            if (isFeatureEnabled('activity-feed')) {
              setSelectedActivity(item);
            } else {
              dispatch(setAttemptedProFeature('Rich activity data'));
            }
          }}
        >
          <ActivityItem studentId={studentId} activity={item} />
        </ItemContainer>
      );
    }
  }

  if (!session) return null;
  return (
    <div
      id={`session-${sessionId}`}
      className="flex w-full mt-2 scroll-mt-[68px] group"
    >
      <div className="flex flex-col items-center">
        <div className="flex items-center justify-center w-7 h-7 rounded-lg bg-purple-400 text-white mb-1">
          <MagicWandIcon size={20} />
        </div>
        <div className="h-[calc(100%-44px)] bg-border w-px" />
      </div>

      <div className="flex flex-col gap-2 ml-2 w-full mb-3 relative">
        {highlight && (
          <div className="absolute -top-1.5 -left-1.5 h-[calc(100%+12px)] w-[calc(100%+12px)] rounded-lg bg-primary opacity-10 -z-10" />
        )}
        <div className="flex items-center text-sm font-medium -mt-1 h-8">
          <span>{studentName} played a session of Spellcaster</span>
          <span className="ml-2 text-neutral-400 text-xs">
            {formatStartedAt(session.startedAt)}
          </span>
          {!shareLink && (
            <div>
              <Tooltip>
                <TooltipTrigger asChild>
                  <Button
                    variant="ghost"
                    className="ml-2 px-1 py-1 h-fit hidden group-hover:flex"
                    onClick={() => handleOpenShareDialog('session')}
                  >
                    <LinkIcon size={16} />
                  </Button>
                </TooltipTrigger>
                <TooltipContent>Share session</TooltipContent>
              </Tooltip>
              <div className="h-8 group-hover:hidden" />
            </div>
          )}
          {!shareLink && activities.length > 0 && (
            <FeatureSwitch
              feature="learner-notes"
              enabled={
                <Tooltip>
                  <TooltipTrigger asChild>
                    <span>
                      <Button
                        variant="ghost"
                        className="ml-2 px-1 py-1 h-fit hidden group-hover:flex"
                        onClick={handleGenerateSummary}
                        disabled={isGenerating}
                      >
                        <FileTextIcon className="size-4" />
                      </Button>
                    </span>
                  </TooltipTrigger>
                  <TooltipContent>Generate Session Summary</TooltipContent>
                </Tooltip>
              }
              disabled={null}
            />
          )}
        </div>

        {isLoading ? (
          <div className="flex justify-between bg-white border border-border rounded-lg p-3 mr-2">
            <div className="flex flex-col justify-between h-full w-full gap-1">
              <Skeleton className="h-3 w-32" />
              <Skeleton className="h-4 w-28" />
              <Skeleton className="h-3 w-36" />
            </div>
            <div className="flex flex-col gap-1">
              <Skeleton className="h-8 w-20" />
              <Skeleton className="h-3 w-22" />
            </div>
          </div>
        ) : combinedItems.length ? (
          combinedItems.map((item) => (
            <div key={item.id}>{renderItem(item)}</div>
          ))
        ) : (
          <div className="text-xs font-medium text-muted-foreground">
            No activities recorded
          </div>
        )}
      </div>

      <ActivityDialog
        studentId={studentId}
        activity={selectedActivity}
        onClose={() => setSelectedActivity(null)}
        onShare={() => {
          if (selectedActivity) {
            handleOpenShareDialog('activity', selectedActivity);
          }
          setPrevSelectedActivity(selectedActivity);
          setSelectedActivity(null);
        }}
      />

      <PlacementTestDialogue
        result={selectedPlacementTestResult}
        onClose={() => setSelectedPlacementTestResult(null)}
      />

      <ShareLinkDialogue
        open={shareLinkDialogueOpen}
        onClose={() => {
          setShareLinkDialogueOpen(false);
          setSelectedActivity(prevSelectedActivity);
          setPrevSelectedActivity(null);
        }}
        studentId={studentId}
        type={prevSelectedActivity ? 'activity' : 'session'}
        resourceId={prevSelectedActivity ? prevSelectedActivity.id : sessionId}
      />
    </div>
  );
}

const ItemContainer = tw.div`flex justify-between border border-border rounded-lg p-3 mr-2 cursor-pointer transition-colors hover:bg-accent`;
